import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { InferenceRequest } from "../types/inference-request";
import { getInferenceBaseUrl } from "../../../config/config";
import { sec } from "../../../utils/security";
import { InferenceResponse } from "../types/inference-response";
import type { HostKind } from "../../../platform/types";

/**
 * The source tag the inference API bills each Office host's calls under: "word-addin",
 * "excel-addin", "powerpoint-addin" or "outlook-addin", each its own billing resource
 * type, so add-in usage shows per host in analytics rather than all as Word. (Ticket 237)
 */
export const addInSource = (host: HostKind): string => `${host}-addin`;

/**
 * Tags an inference request with the running host's source. The tag is authoritative
 * (applied after the caller's fields).
 */
export const withAddInSource = (request: InferenceRequest, host: HostKind): InferenceRequest => ({
  ...request,
  source: addInSource(host),
});

export const inferenceApi = createApi({
  reducerPath: "inferenceApi",
  baseQuery: async (args, api, extraOptions) =>
    fetchBaseQuery({
      baseUrl: getInferenceBaseUrl(),
      prepareHeaders: async (headers) => {
        const token = await sec.getAccessTokenSilently()();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
      },
    })(args, api, extraOptions),

  endpoints: (builder) => ({
    runInference: builder.mutation<
      InferenceResponse,
      { modelObjectIdentifier: string; request: InferenceRequest; host: HostKind }
    >({
      query: ({ modelObjectIdentifier, request, host }) => ({
        url: `inference/completions/${modelObjectIdentifier}`,
        method: "POST",
        // Tag every add-in inference call with the host it came from so billing is
        // attributed to that host's resource type in analytics. (Ticket 237)
        body: withAddInSource(request, host),
      }),
    }),
  }),
});

export const { useRunInferenceMutation } = inferenceApi;
